import { useState, useEffect, useRef } from "react";
import * as api from "./api";
import { COLORS, baseStyles } from "./shared/constants";
import { canDo } from "./shared/utils";
import LoginScreen from "./components/LoginScreen";
import Header from "./components/Header";
import HomeScreen from "./components/HomeScreen";
import PatientList from "./components/PatientList";
import PatientForm from "./components/PatientForm";
import PatientDetail from "./components/PatientDetail";
import TreatmentSessionScreen from "./components/TreatmentSessionScreen";
import SettingsScreen from "./components/settings/SettingsScreen";
import ProtocolList from "./components/ProtocolList";
import ProtocolForm from "./components/ProtocolForm";
import DeviceControlScreen from "./components/DeviceControlScreen";
import MTDeterminationScreen from "./components/MTDeterminationScreen";

// ─── App ─────────────────────────────────────────────────────
export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [screen, setScreen] = useState("LOGIN");
  const [patients, setPatients] = useState([]);
  const [providers, setProviders] = useState([]);
  const [editingPatient, setEditingPatient] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [editingProtocol, setEditingProtocol] = useState(null);
  const [users, setUsers] = useState([]);
  const [coils, setCoils] = useState([]);
  const [treatmentSpots, setTreatmentSpots] = useState([]);
  const [mtSpots, setMtSpots] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [clinicInfo, setClinicInfo] = useState({});
  const [treatments, setTreatments] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [reminders, setReminders] = useState([]);

  const [assessmentScales, setAssessmentScales] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [pendingAssessment, setPendingAssessment] = useState(null); // { treatmentId, scaleId } — opens assessment form on PatientDetail
  const [mtSession, setMtSession] = useState(null); // { treatment, protocol, patient, previousMT } — for MT Determination screen
  const [needsSetup, setNeedsSetup] = useState(null); // null = checking, true = first-run, false = normal
  const [setupForm, setSetupForm] = useState({ first_name: "", last_name: "", email: "", pin: "", confirmPin: "" });
  const [setupError, setSetupError] = useState("");

  // S3: Idle timeout
  const idleTimerRef = useRef(null);
  const idleTimeoutMinutes = useRef(15);
  const lastActivityRef = useRef(Date.now());
  const screenRef = useRef(screen);
  screenRef.current = screen;

  // Always use latest user data (permissions may have been updated by MD)
  const liveCurrentUser = currentUser ? users.find((u) => u.id === currentUser.id) || currentUser : null;

  // S3: Register auth expiry callback
  useEffect(() => {
    api.setOnAuthExpired(() => handleLogout());
  }, []);

  // S6: Check first-run setup on mount
  useEffect(() => {
    api.getSetupStatus().then(res => {
      setNeedsSetup(res.needs_setup);
    }).catch(() => setNeedsSetup(false));
  }, []);

  // S3: Idle timeout — track activity, auto-logout
  useEffect(() => {
    if (!currentUser) return;

    const resetIdle = () => { lastActivityRef.current = Date.now(); };
    const events = ["mousedown", "touchstart", "keydown", "scroll"];
    events.forEach(e => window.addEventListener(e, resetIdle, { passive: true }));

    idleTimerRef.current = setInterval(() => {
      // Never auto-logout during an active treatment or MT determination
      if (screenRef.current === "SESSION" || screenRef.current === "MT_DETERMINATION") {
        lastActivityRef.current = Date.now();
        return;
      }
      const idleMs = Date.now() - lastActivityRef.current;
      const timeoutMs = idleTimeoutMinutes.current * 60 * 1000;
      if (timeoutMs > 0 && idleMs >= timeoutMs) {
        handleLogout();
      }
    }, 30000); // Check every 30 seconds

    // Fetch idle timeout setting from server
    api.getAuthConfig().then(res => {
      if (res.idle_timeout_minutes) idleTimeoutMinutes.current = res.idle_timeout_minutes;
    }).catch(() => {});

    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdle));
      if (idleTimerRef.current) clearInterval(idleTimerRef.current);
    };
  }, [currentUser]);

  // JWT keepalive during treatment sessions — refreshes token every 5 min
  // so it never expires during the zero-API-call ACTIVE treatment phase
  const keepaliveRef = useRef(null);
  useEffect(() => {
    if (screen === "SESSION" || screen === "MT_DETERMINATION") {
      // Start keepalive
      keepaliveRef.current = setInterval(() => {
        api.refreshTokenKeepalive();
      }, 5 * 60 * 1000); // Every 5 minutes
      // Also refresh immediately on entering treatment screen
      api.refreshTokenKeepalive();
    } else {
      // Clear keepalive when leaving treatment screens
      if (keepaliveRef.current) {
        clearInterval(keepaliveRef.current);
        keepaliveRef.current = null;
      }
    }
    return () => {
      if (keepaliveRef.current) {
        clearInterval(keepaliveRef.current);
        keepaliveRef.current = null;
      }
    };
  }, [screen]);

  // Load all data from API after login
  const loadAllData = async () => {
    try {
      const [usersRes, patientsRes, providersRes, protocolsRes, coilsRes, spotsRes, mtRes, dxRes, clinicRes, scalesRes] = await Promise.all([
        api.getUsers(),
        api.getPatients(),
        api.getProviders(),
        api.getProtocols(),
        api.getSettings("coils"),
        api.getSettings("treatment-spots"),
        api.getSettings("mt-spots"),
        api.getSettings("diagnoses"),
        api.getClinic(),
        api.getAssessmentScales(),
      ]);
      setUsers(usersRes.users || []);
      setPatients(patientsRes.patients || []);
      setProviders(providersRes.providers || []);
      setProtocols(protocolsRes.protocols || []);
      setCoils(coilsRes.items || []);
      setTreatmentSpots(spotsRes.items || []);
      setMtSpots(mtRes.items || []);
      setDiagnoses(dxRes.items || []);
      setClinicInfo(clinicRes.clinic || {});
      setAssessmentScales(scalesRes.scales || []);
    } catch (e) {
      if (e.status === 401) { handleLogout(); return; }
      console.error("Failed to load data:", e);
    }
  };

  // Load patient-specific data (treatments, sessions, reminders)
  const loadPatientData = async (patientId) => {
    try {
      const [txRes, sessRes, remRes, assessRes] = await Promise.all([
        api.getPatientTreatments(patientId),
        api.getPatientSessions(patientId),
        api.getReminders({ patient_id: patientId }),
        api.getPatientAssessments(patientId),
      ]);
      setTreatments((prev) => {
        const otherTx = prev.filter((t) => t.patient_id !== patientId);
        return [...otherTx, ...((txRes && txRes.treatments) || [])];
      });
      setSessions((prev) => {
        const otherSess = prev.filter((s) => s.patient_id !== patientId);
        return [...otherSess, ...((sessRes && sessRes.sessions) || [])];
      });
      setReminders((prev) => {
        const txIds = ((txRes && txRes.treatments) || []).map((t) => t.id);
        const otherRems = prev.filter((r) => !txIds.includes(r.treatment_assignment_id));
        return [...otherRems, ...((remRes && remRes.reminders) || [])];
      });
      setAssessments((prev) => {
        const other = prev.filter((a) => a.patient_id !== patientId);
        return [...other, ...((assessRes && assessRes.assessments) || [])];
      });
    } catch (e) {
      if (e.status === 401) { handleLogout(); return; }
      console.error("Failed to load patient data:", e);
    }
  };

  const handleLogin = async (user) => {
    setCurrentUser(user);
    lastActivityRef.current = Date.now(); // Reset idle timer on login
    // Show home screen immediately — data loads in background
    if (user.default_landing_view === "PATIENT_LIST") {
      setScreen("PATIENT_LIST");
    } else {
      setScreen("HOME");
    }
    loadAllData();
  };

  const handleLogout = async () => {
    // Release device lock FIRST (clears the lock check gate), then
    // stop treatment (disarms MagPro). Order matters: stop-treatment
    // route checks the device lock and rejects requests without a
    // matching session_id. Releasing the lock first removes that gate.
    try {
      await api.deviceReleaseLock(null);
    } catch (e) { /* ignore — no lock held */ }
    try {
      await api.deviceStopTreatment();
    } catch (e) { /* ignore — no active treatment */ }
    await api.logout();
    setCurrentUser(null);
    setScreen("LOGIN");
    setPatients([]);
    setProviders([]);
    setProtocols([]);
    setUsers([]);
    setTreatments([]);
    setSessions([]);
    setReminders([]);
    setAssessments([]);
  };

  // S6: Handle first-run MD setup
  const handleSetupSubmit = async () => {
    setSetupError("");
    if (setupForm.pin !== setupForm.confirmPin) {
      setSetupError("PINs don't match");
      return;
    }
    if (setupForm.pin.length !== 6 || !/^\d{6}$/.test(setupForm.pin)) {
      setSetupError("PIN must be exactly 6 digits");
      return;
    }
    if (!setupForm.first_name.trim() || !setupForm.last_name.trim()) {
      setSetupError("Name is required");
      return;
    }
    try {
      await api.createInitialMD({
        first_name: setupForm.first_name,
        last_name: setupForm.last_name,
        email: setupForm.email,
        pin: setupForm.pin,
      });
      setNeedsSetup(false);
    } catch (e) {
      setSetupError(e.error || "Setup failed");
    }
  };

  const handleSavePatient = async (patient) => {
    try {
      if (patient.id) {
        const res = await api.updatePatient(patient.id, patient);
        setPatients((prev) => prev.map((p) => (p.id === patient.id ? { ...p, ...res.patient } : p)));
        if (selectedPatient?.id === patient.id) setSelectedPatient({ ...selectedPatient, ...res.patient });
      } else {
        const res = await api.createPatient(patient);
        setPatients((prev) => [...prev, res.patient]);
      }
    } catch (e) { console.error("Save patient:", e); }
    setEditingPatient(null);
    setScreen("PATIENT_LIST");
    loadAllData(); // Refresh patient list with enriched data
  };

  const handleAddProvider = async (provider) => {
    try {
      const res = await api.createProvider(provider);
      setProviders((prev) => [...prev, res.provider]);
      return res.provider;
    } catch (e) { console.error("Add provider:", e); return provider; }
  };

  const handleEditProvider = async (updated) => {
    try {
      const res = await api.updateProvider(updated.id, updated);
      setProviders((prev) => prev.map((p) => (p.id === updated.id ? res.provider : p)));
    } catch (e) { console.error("Edit provider:", e); }
  };

  const handleDeleteProvider = async (id) => {
    try {
      await api.deleteProvider(id);
      setProviders((prev) => prev.filter((p) => p.id !== id));
    } catch (e) { console.error("Delete provider:", e); }
  };

  const handleSaveProtocol = async (protocol) => {
    try {
      if (protocol.id && protocols.some((p) => p.id === protocol.id)) {
        const res = await api.updateProtocol(protocol.id, protocol);
        setProtocols((prev) => prev.map((p) => (p.id === protocol.id ? res.protocol : p)));
      } else {
        const res = await api.createProtocol(protocol);
        setProtocols((prev) => [...prev, res.protocol]);
      }
    } catch (e) { console.error("Save protocol:", e); }
    setEditingProtocol(null);
  };

  const handleCopyProtocol = async (proto) => {
    try {
      const res = await api.copyProtocol(proto.id, { name: `${proto.name} (copy)` });
      setProtocols((prev) => [...prev, res.protocol]);
    } catch (e) { console.error("Copy protocol:", e); }
  };

  const handleDeleteProtocol = async (id) => {
    try {
      await api.deleteProtocol(id);
      setProtocols((prev) => prev.filter((p) => p.id !== id));
    } catch (e) { console.error("Delete protocol:", e); }
  };

  // ─── First-run setup screen ──────────────────────────────────
  if (needsSetup === null) {
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: COLORS.bg }}>
      <p style={{ color: COLORS.textSecondary }}>Loading...</p>
    </div>;
  }

  if (needsSetup) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: COLORS.bg }}>
        <div style={{ background: COLORS.card, borderRadius: 16, padding: 40, width: 400, boxShadow: "0 4px 24px rgba(0,0,0,0.3)" }}>
          <h2 style={{ color: COLORS.text, textAlign: "center", marginBottom: 8 }}>Fox TMS Clinic EHR</h2>
          <p style={{ color: COLORS.textSecondary, textAlign: "center", marginBottom: 24 }}>First-Run Setup — Create MD Account</p>
          {setupError && <div style={{ background: "#5c1a1a", color: "#ff6b6b", padding: "8px 12px", borderRadius: 8, marginBottom: 16, textAlign: "center" }}>{setupError}</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input placeholder="First Name" value={setupForm.first_name} onChange={e => setSetupForm(f => ({...f, first_name: e.target.value}))} style={{ ...baseStyles.input }} />
            <input placeholder="Last Name" value={setupForm.last_name} onChange={e => setSetupForm(f => ({...f, last_name: e.target.value}))} style={{ ...baseStyles.input }} />
            <input placeholder="Email (optional)" value={setupForm.email} onChange={e => setSetupForm(f => ({...f, email: e.target.value}))} style={{ ...baseStyles.input }} />
            <input placeholder="6-Digit PIN" type="password" maxLength={6} value={setupForm.pin} onChange={e => setSetupForm(f => ({...f, pin: e.target.value.replace(/\D/g, "")}))} style={{ ...baseStyles.input }} />
            <input placeholder="Confirm PIN" type="password" maxLength={6} value={setupForm.confirmPin} onChange={e => setSetupForm(f => ({...f, confirmPin: e.target.value.replace(/\D/g, "")}))} style={{ ...baseStyles.input }} />
            <button onClick={handleSetupSubmit} style={{ ...baseStyles.btnPrimary, marginTop: 8 }}>Create MD Account</button>
          </div>
        </div>
      </div>
    );
  }

  if (screen === "LOGIN") {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <div style={{ ...baseStyles, background: COLORS.bg, minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <Header user={liveCurrentUser} onLogout={handleLogout} onNavigate={setScreen} />
      {screen === "HOME" && (
        <HomeScreen
          currentUser={liveCurrentUser}
          patients={patients}
          onSelectPatient={(p) => {
            setSelectedPatient(p);
            loadPatientData(p.id);
            setScreen("PATIENT_DETAIL");
          }}
          onNavigate={(s) => {
            if (s === "NEW_PATIENT") {
              setEditingPatient(null);
            }
            setScreen(s);
          }}
        />
      )}
      {screen === "PATIENT_LIST" && (
        <PatientList
          patients={patients}
          providers={providers}
          users={users}
          treatments={treatments}
          reminders={reminders}
          currentUser={liveCurrentUser}
          onNavigate={setScreen}
          onSelectPatient={async (p) => {
            setSelectedPatient(p);
            setScreen("PATIENT_DETAIL");
            await loadPatientData(p.id);
          }}
          onEditPatient={(p) => {
            setEditingPatient(p);
            setScreen("EDIT_PATIENT");
          }}
        />
      )}
      {(screen === "NEW_PATIENT" || screen === "EDIT_PATIENT") && (
        <PatientForm
          patient={editingPatient}
          providers={providers}
          diagnoses={diagnoses}
          onSave={handleSavePatient}
          onCancel={() => selectedPatient ? setScreen("PATIENT_DETAIL") : setScreen("PATIENT_LIST")}
          onAddProvider={handleAddProvider}
          onEditProvider={handleEditProvider}
          onDeleteProvider={handleDeleteProvider}
        />
      )}
      {screen === "PATIENT_DETAIL" && selectedPatient && (
        <PatientDetail
          patient={selectedPatient}
          treatments={treatments.filter((t) => t.patient_id === selectedPatient.id)}
          protocols={protocols}
          diagnoses={diagnoses}
          coils={coils}
          treatmentSpots={treatmentSpots}
          mtSpots={mtSpots}
          users={users}
          currentUser={liveCurrentUser}
          reminders={reminders}
          providers={providers}
          clinicInfo={clinicInfo}
          onNavigate={setScreen}
          onEditPatient={() => {
            setEditingPatient(selectedPatient);
            setScreen("EDIT_PATIENT");
          }}
          onAssignTreatment={async (tx) => {
            try {
              const res = await api.assignTreatment(tx);
              setTreatments((prev) => [...prev, res.treatment]);
              if (res.treatment.reminders) {
                setReminders((prev) => [...prev, ...res.treatment.reminders]);
              }
            } catch (e) { console.error("Assign treatment:", e); }
          }}
          onUpdateTreatment={async (id, updates) => {
            try {
              const res = await api.updateTreatment(id, updates);
              setTreatments((prev) => prev.map((t) => t.id === id ? res.treatment : t));
            } catch (e) { console.error("Update treatment:", e); }
          }}
          onStartSession={(treatment) => {
            const proto = protocols.find((p) => p.id === treatment.protocol_id);
            // Clear any stale MT-screen state from a previous treatment.
            // mtSession.returnedTxRatio in particular would otherwise persist
            // across treatments — e.g. picking 100% on rTMS would carry into
            // an iTBS treatment opened next, even though the iTBS treatment
            // has its own (or the protocol's default) ratio.
            setMtSession(null);
            setActiveSession({ treatment, protocol: proto, patient: selectedPatient, deviceSessionId: `session-${treatment.id}-${Date.now()}` });
            setScreen("SESSION");
          }}
          onSaveReminder={async (rem) => {
            try {
              if (rem.status === "PENDING" && reminders.some((r) => r.id === rem.id && r.status === "DUE")) {
                // Clearing a DUE reminder
                const res = await api.clearReminder(rem.id);
                setReminders((prev) => prev.map((r) => r.id === rem.id ? res.reminder : r));
              } else if (reminders.some((r) => r.id === rem.id)) {
                const res = await api.updateReminder(rem.id, rem);
                setReminders((prev) => prev.map((r) => r.id === rem.id ? res.reminder : r));
              } else {
                const res = await api.createReminder(rem);
                setReminders((prev) => [...prev, res.reminder]);
              }
            } catch (e) { console.error("Save reminder:", e); }
          }}
          onDeleteReminder={async (id) => {
            try {
              await api.deleteReminder(id);
              setReminders((prev) => prev.filter((r) => r.id !== id));
            } catch (e) { console.error("Delete reminder:", e); }
          }}
          sessions={sessions}
          assessmentScales={assessmentScales}
          assessments={assessments.filter((a) => a.patient_id === selectedPatient.id)}
          onSubmitAssessment={async (data) => {
            try {
              const res = await api.createAssessment(data);
              setAssessments((prev) => [...prev, res.assessment]);
              // Reload to get updated item-level data and reminder state
              await loadPatientData(selectedPatient.id);
            } catch (e) { console.error("Submit assessment:", e); }
          }}
          onGenerateLink={async (data) => {
            try {
              const res = await api.generateAssessmentLink(data);
              return res;
            } catch (e) { console.error("Generate link:", e); return null; }
          }}
          onRefreshAssessments={async () => {
            try {
              await loadPatientData(selectedPatient.id);
            } catch (e) { console.error("Refresh patient data:", e); }
          }}
          pendingAssessment={pendingAssessment}
          onClearPendingAssessment={() => setPendingAssessment(null)}
          onAmendSession={async (sessionId, data) => {
            const res = await api.amendSession(sessionId, data);
            // Update session in state with new values
            setSessions((prev) => prev.map((s) => s.id === sessionId ? { ...s, ...res.session } : s));
            // Reload to get full amendment data
            await loadPatientData(selectedPatient.id);
          }}
          onAmendAssessment={async (adminId, data) => {
            const res = await api.amendAssessment(adminId, data);
            // Update assessment in state with new values
            setAssessments((prev) => prev.map((a) => a.id === adminId ? { ...a, ...res.assessment } : a));
            // Reload to get full amendment data
            await loadPatientData(selectedPatient.id);
          }}
          onVoidSession={async (sessionId, data) => {
            const res = await api.voidSession(sessionId, data);
            setSessions((prev) => prev.map((s) => s.id === sessionId ? { ...s, ...res.session } : s));
            await loadPatientData(selectedPatient.id);
          }}
          onVoidAssessment={async (adminId, data) => {
            const res = await api.voidAssessment(adminId, data);
            setAssessments((prev) => prev.map((a) => a.id === adminId ? { ...a, ...res.assessment } : a));
            await loadPatientData(selectedPatient.id);
          }}
        />
      )}
      {screen === "SESSION" && activeSession && (
        <TreatmentSessionScreen
          patient={activeSession.patient}
          treatment={activeSession.treatment}
          protocol={activeSession.protocol}
          deviceSessionId={activeSession.deviceSessionId}
          currentUser={liveCurrentUser}
          allSessions={sessions.filter((s) => s.patient_id === activeSession.patient.id)}
          dueReminders={reminders.filter((r) => r.treatment_assignment_id === activeSession.treatment.id && r.is_active && r.status === "DUE")}
          onComplete={async (sessionRecord) => {
            try {
              const res = await api.createSession({
                treatment_assignment_id: activeSession.treatment.id,
                motor_threshold_value: sessionRecord.motor_threshold_value,
                delivered_total_pulses: sessionRecord.delivered_total_pulses,
                final_intensity_pct: sessionRecord.final_intensity_pct,
                status: sessionRecord.status,
                interruption_reason: sessionRecord.interruption_reason,
                session_notes: sessionRecord.session_notes,
                treatment_ratio_pct: sessionRecord.treatment_ratio_pct,
              });
              setSessions((prev) => [...prev, res.session]);
              // Reload treatment + reminders from server (has side effects)
              await loadPatientData(activeSession.patient.id);
              // Reload patient list to update Last Tx, Tx #, Last Treated By
              const patientsRes = await api.getPatients();
              setPatients(patientsRes.patients || []);
            } catch (e) { console.error("Save session:", e); }
            setActiveSession(null);
            setScreen("PATIENT_DETAIL");
          }}
          onDiscard={async () => {
            try {
              await api.discardSession({
                treatment_assignment_id: activeSession.treatment.id,
                reason: "Discarded by operator",
              });
            } catch (e) { console.error("Discard:", e); }
            setActiveSession(null);
            setScreen("PATIENT_DETAIL");
          }}
          onCancel={() => {
            setActiveSession(null);
            setScreen("PATIENT_DETAIL");
          }}
          onOpenAssessment={(assessmentInfo) => {
            setPendingAssessment(assessmentInfo);
            setActiveSession(null);
            setScreen("PATIENT_DETAIL");
          }}
          assessmentScales={assessmentScales}
          onDetermineMT={() => {
            setMtSession({
              treatment: activeSession.treatment,
              protocol: activeSession.protocol,
              patient: activeSession.patient,
              deviceSessionId: activeSession.deviceSessionId,
            });
            setScreen("MT_DETERMINATION");
          }}
          returnedMT={(mtSession?.treatment?.id === activeSession.treatment.id ? mtSession?.returnedMT : null) || null}
          returnedTxRatio={(mtSession?.treatment?.id === activeSession.treatment.id ? mtSession?.returnedTxRatio : null) || null}
          onClearReturnedMT={() => setMtSession((prev) => prev ? { ...prev, returnedMT: null } : null)}
        />
      )}
      {screen === "MT_DETERMINATION" && mtSession && (
        <MTDeterminationScreen
          patient={mtSession.patient}
          treatment={mtSession.treatment}
          protocol={mtSession.protocol}
          deviceSessionId={mtSession.deviceSessionId}
          coils={coils}
          mtSpots={mtSpots}
          dueReminders={reminders.filter((r) => r.treatment_assignment_id === mtSession.treatment.id && r.is_active && r.status === "DUE")}
          assessmentScales={assessmentScales}
          returnedTxRatio={mtSession.returnedTxRatio || null}
          onOpenAssessment={(info) => {
            setScreen("SESSION");
            // Pass assessment info back so session screen can handle it
          }}
          onSaveMT={(value, mtCoil, mtSpot, txRatioPct) => {
            setMtSession((prev) => ({
              ...prev,
              returnedMT: value,
              returnedMTCoil: mtCoil,
              returnedMTSpot: mtSpot,
              returnedTxRatio: txRatioPct,
            }));
          }}
          onTreatmentUpdated={(updated) => {
            // Merge the freshly-persisted treatment row into the
            // treatments[] cache so navigating back to this treatment
            // shows the just-saved MT / ratio / coil / spot.
            setTreatments((prev) =>
              prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t))
            );
          }}
          onBack={() => {
            setScreen("SESSION");
          }}
        />
      )}
      {screen === "PROTOCOLS" && (
        <ProtocolList
          protocols={protocols}
          currentUser={liveCurrentUser}
          onNavigate={setScreen}
          onCopy={handleCopyProtocol}
          onDelete={handleDeleteProtocol}
          onEdit={(p) => {
            setEditingProtocol(p);
            setScreen("EDIT_PROTOCOL");
          }}
          onNew={() => {
            setEditingProtocol(null);
            setScreen("NEW_PROTOCOL");
          }}
        />
      )}
      {(screen === "NEW_PROTOCOL" || screen === "EDIT_PROTOCOL") && (
        <ProtocolForm
          protocol={editingProtocol}
          currentUser={liveCurrentUser}
          coils={coils}
          treatmentSpots={treatmentSpots}
          mtSpots={mtSpots}
          onSave={(p) => { handleSaveProtocol(p); setScreen("PROTOCOLS"); }}
          onCancel={() => setScreen("PROTOCOLS")}
        />
      )}
      {screen === "DEVICE_CONTROL" && (
        <DeviceControlScreen currentUser={liveCurrentUser} onNavigate={setScreen} protocols={protocols} />
      )}
      {screen === "SETTINGS" && (
        <SettingsScreen
          currentUser={liveCurrentUser}
          clinicInfo={clinicInfo}
          onSaveClinicInfo={setClinicInfo}
          users={users}
          onSaveUser={async (u) => {
            try {
              if (users.some((x) => x.id === u.id)) {
                // Update existing user
                const res = await api.updateUser(u.id, { first_name: u.first_name, last_name: u.last_name, email: u.email, role: u.role, pin: u.pin || undefined });
                // Also update permissions if operator
                if (u.role === "OPERATOR" && u.permissions) {
                  await api.updatePermissions(u.id, u.permissions);
                }
                // Reload users to get fresh data
                const usersRes = await api.getUsers();
                setUsers(usersRes.users || []);
              } else {
                // Create new user
                const res = await api.createUser({ first_name: u.first_name, last_name: u.last_name, email: u.email, role: u.role, pin: u.pin });
                if (u.role === "OPERATOR" && u.permissions) {
                  await api.updatePermissions(res.user.id, u.permissions);
                }
                const usersRes = await api.getUsers();
                setUsers(usersRes.users || []);
              }
            } catch (e) { console.error("Save user:", e); }
          }}
          onDeactivateUser={async (id) => {
            try {
              await api.deactivateUser(id);
              const usersRes = await api.getUsers();
              setUsers(usersRes.users || []);
            } catch (e) { console.error("Deactivate user:", e); }
          }}
          providers={providers}
          onSaveProvider={async (p) => {
            try {
              if (providers.some((x) => x.id === p.id)) {
                const res = await api.updateProvider(p.id, p);
                setProviders((prev) => prev.map((x) => x.id === p.id ? res.provider : x));
              } else {
                const res = await api.createProvider(p);
                setProviders((prev) => [...prev, res.provider]);
              }
            } catch (e) { console.error("Save provider:", e); }
          }}
          onDeleteProvider={async (id) => {
            try {
              await api.deleteProvider(id);
              setProviders((prev) => prev.filter((p) => p.id !== id));
            } catch (e) { console.error("Delete provider:", e); }
          }}
          coils={coils}
          onSaveCoils={setCoils}
          treatmentSpots={treatmentSpots}
          onSaveTreatmentSpots={setTreatmentSpots}
          mtSpots={mtSpots}
          onSaveMtSpots={setMtSpots}
          diagnoses={diagnoses}
          onSaveDiagnoses={setDiagnoses}
          onNavigate={setScreen}
          protocols={protocols}
          onCopyProtocol={handleCopyProtocol}
          onDeleteProtocol={handleDeleteProtocol}
          onSaveProtocol={handleSaveProtocol}
          onReorderProtocols={(order) => {
            setProtocols((prev) => prev.map((p) => {
              const item = order.find((o) => o.id === p.id);
              return item ? { ...p, sort_order: item.sort_order } : p;
            }));
          }}
          assessmentScales={assessmentScales}
          onSaveScale={async (data) => {
            try {
              if (data.id) {
                const res = await api.updateAssessmentScale(data.id, data);
                setAssessmentScales(prev => prev.map(s => s.id === data.id ? res.scale : s));
              } else {
                const res = await api.createAssessmentScale(data);
                setAssessmentScales(prev => [...prev, res.scale]);
              }
            } catch (e) { console.error("Save scale:", e); }
          }}
          onCopyScale={async (id) => {
            try {
              const res = await api.copyAssessmentScale(id);
              setAssessmentScales(prev => [...prev, res.scale]);
            } catch (e) { console.error("Copy scale:", e); }
          }}
          onDeleteScale={async (id) => {
            try {
              await api.deleteAssessmentScale(id);
              setAssessmentScales(prev => prev.filter(s => s.id !== id));
            } catch (e) { console.error("Delete scale:", e); }
          }}
        />
      )}
    </div>
  );
}

// ─── Login Screen ────────────────────────────────────────────
